/**
 * The labelled toggle pill — an icon plus a Polish label that flips between two
 * states, backed by an IndexedDB store. Two of these exist in the app and they had
 * grown byte-identical wiring:
 *
 *   - the topic header's "Dodaj do ulubionych" / "Usuń z ulubionych" (heart, red),
 *   - the phrase preview's "Dodaj do słownika" / "Usuń ze słownika" (star, amber).
 *
 * The contract worth having in one place is the one both were careful about: the
 * pill's state is **always** whatever the store says. The initial state is read
 * from IndexedDB, and every flip is drawn from what the write actually persisted —
 * never from a local boolean that could drift from the database. A failed read or
 * write leaves the pill on its last known state instead of lying about it.
 */

/**
 * Markup for the pill. Wire it with {@link wireTogglePill}, which is what fills in
 * the icon and label for the current state — the attributes here are only the
 * "off" state, so the button reads correctly before the store answers.
 *
 * @param {{ id?: string, offLabel: string, offIcon: string }} options
 *   - id: optional element id, for a screen that wants to find it by one.
 *   - offLabel / offIcon: what to show until the store's answer arrives.
 */
export function togglePillHtml({ id = '', offLabel, offIcon }) {
  return `
    <button ${id ? `id="${id}"` : ''} data-toggle-pill type="button" aria-pressed="false"
      aria-label="${offLabel}"
      class="inline-flex items-center gap-2 rounded-full border-2 border-brand bg-white py-2 pl-3 pr-4 text-sm font-semibold text-neutral-800 transition hover:bg-lilac/40 active:scale-[0.98] disabled:opacity-60">
      <span data-toggle-pill-icon class="text-neutral-600">${offIcon}</span>
      <span data-toggle-pill-label>${offLabel}</span>
    </button>`;
}

/**
 * Bind a pill rendered by {@link togglePillHtml} to its store.
 *
 * @param {?HTMLElement} root Element containing the pill (or the pill itself).
 * @param {object} options
 *   - read: `() => Promise<boolean>` — the persisted state.
 *   - write: `() => Promise<boolean>` — flip it, resolving with the **new** state
 *     as it landed in the store.
 *   - labels: `[off, on]` Polish labels; also used as the `aria-label`.
 *   - icons: `[off, on]` icon markup.
 *   - onClass: Tailwind text-colour class for the "on" icon (e.g. `text-red-500`).
 * @returns {Promise<void>} Resolves once the initial state has been applied.
 */
export async function wireTogglePill(root, { read, write, labels, icons, onClass }) {
  const btn = root?.matches?.('[data-toggle-pill]')
    ? root
    : root?.querySelector('[data-toggle-pill]');
  if (!btn) return;

  const iconEl = btn.querySelector('[data-toggle-pill-icon]');
  const labelEl = btn.querySelector('[data-toggle-pill-label]');
  let on = false;

  const applyState = () => {
    const label = labels[on ? 1 : 0];
    iconEl.innerHTML = icons[on ? 1 : 0];
    iconEl.classList.toggle(onClass, on);
    // Must stay the same class the markup ships with — see `togglePillHtml`.
    iconEl.classList.toggle('text-neutral-600', !on);
    labelEl.textContent = label;
    btn.setAttribute('aria-pressed', String(on));
    btn.setAttribute('aria-label', label);
  };

  btn.addEventListener('click', async () => {
    btn.disabled = true;
    try {
      on = await write();
    } catch {
      /* IndexedDB unavailable — leave the pill on its last persisted state */
    }
    btn.disabled = false;
    applyState();
  });

  try {
    on = await read();
  } catch {
    /* IndexedDB unavailable — fall back to the "off" state */
  }
  applyState();
}
